import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, inr } from "@/lib/api";
import { fuzzyFilter } from "@/lib/fuzzy";
import { PageHeader } from "@/components/Bits";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast, Toaster } from "sonner";
import { CaretRight, CaretDown } from "@phosphor-icons/react";

export default function Ledgers() {
    const [groups, setGroups] = useState([]);
    const [ledgers, setLedgers] = useState([]);
    const [balances, setBalances] = useState({});
    const [q, setQ] = useState("");
    const [expanded, setExpanded] = useState({});   // {group_id: bool}
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", group_id: "", party_type: "generic", gstin: "" });

    const load = async () => {
        try {
            const [g, l] = await Promise.all([api.get("/account-groups"), api.get("/ledgers", { params: { scope: "browse" } })]);
            setGroups(g.data); setLedgers(l.data);
            // fetch balances (parallel, each with its own catch)
            const bal = await Promise.all(l.data.map((x) => api.get(`/ledgers/${x.id}`).then((r) => [x.id, r.data.closing_balance]).catch(() => [x.id, 0])));
            setBalances(Object.fromEntries(bal));
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        try {
            if (!form.name || !form.group_id) return toast.error("Name and group required");
            await api.post("/ledgers", form);
            toast.success("Ledger created"); setOpen(false); setForm({ name: "", group_id: "", party_type: "generic", gstin: "" }); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // Fuzzy filter runs entirely client-side so "vtrend" instantly matches
    // "V TREND", one-char typos still land on the right ledger, etc.
    const visibleLedgers = useMemo(
        () => fuzzyFilter(ledgers, q, ["name", "group_name", "gstin"]),
        [ledgers, q]
    );

    const grouped = groups.map((g) => ({
        ...g,
        items: visibleLedgers.filter((l) => l.group_id === g.id),
    })).filter((g) => g.items.length > 0);

    // Auto-expand a group when the user is searching (so hits are visible)
    const searchActive = q.trim().length > 0;

    const toggleGroup = (gid) => setExpanded((e) => ({ ...e, [gid]: !e[gid] }));

    const groupBalance = (items) => items.reduce((s, l) => s + (Number(balances[l.id]) || 0), 0);

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader title="Ledgers" sub="Every party, bank, cash and expense head — with a running closing balance."
                right={
                    <>
                        <Input placeholder="Search ledger…" value={q} onChange={(e) => setQ(e.target.value)} className="w-56 h-9" data-testid="ledger-search" />
                        <Dialog open={open} onOpenChange={setOpen}>
                            <DialogTrigger asChild>
                                <Button style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="add-ledger-btn">+ New ledger</Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader><DialogTitle className="font-serif text-2xl">New ledger</DialogTitle></DialogHeader>
                                <div className="space-y-4">
                                    <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-ledger-name" /></div>
                                    <div><Label>Group</Label>
                                        <Select value={form.group_id} onValueChange={(v) => setForm({ ...form, group_id: v })}>
                                            <SelectTrigger data-testid="new-ledger-group"><SelectValue placeholder="Choose group" /></SelectTrigger>
                                            <SelectContent className="max-h-[300px] overflow-y-auto">{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name} · {g.category}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div><Label>Party type</Label>
                                        <Select value={form.party_type} onValueChange={(v) => setForm({ ...form, party_type: v })}>
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="generic">Generic</SelectItem><SelectItem value="vendor">Vendor</SelectItem>
                                                <SelectItem value="customer">Customer</SelectItem><SelectItem value="transporter">Transporter</SelectItem>
                                                <SelectItem value="bank">Bank</SelectItem><SelectItem value="cash">Cash</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div><Label>GSTIN (optional)</Label><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} /></div>
                                </div>
                                <DialogFooter><Button onClick={create} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="new-ledger-submit">Create</Button></DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </>
                } />
            <div className="p-8 space-y-3">
                {grouped.map((g) => {
                    const isOpen = searchActive || !!expanded[g.id];
                    const total = groupBalance(g.items);
                    return (
                    <div key={g.id} className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                        <button
                            onClick={() => toggleGroup(g.id)}
                            className="w-full px-5 py-3 flex items-center justify-between hover:bg-stone-50 transition text-left"
                            data-testid={`group-header-${g.id}`}
                        >
                            <div className="flex items-center gap-3">
                                <span className="text-stone-400">{isOpen ? <CaretDown size={14} /> : <CaretRight size={14} />}</span>
                                <div>
                                    <div className="font-serif text-lg">{g.name}</div>
                                    <div className="text-[10px] uppercase tracking-widest text-stone-500">{g.category}</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-6">
                                <div className="text-xs text-stone-500">{g.items.length} ledger{g.items.length === 1 ? "" : "s"}</div>
                                <div className="se-num text-base" style={{ color: total > 0 ? "#065f46" : total < 0 ? "#9f1239" : "#57534e" }}>
                                    ₹ {inr(total)}
                                </div>
                            </div>
                        </button>
                        {isOpen ? (
                            <table className="se-table" data-testid={`group-${g.id}`}>
                                <thead><tr><th>Name</th><th>GSTIN</th><th className="text-right">Closing balance</th></tr></thead>
                                <tbody>
                                    {g.items.map((l) => (
                                        <tr key={l.id}>
                                            <td><Link to={`/ledgers/${l.id}`} className="hover:underline" style={{ color: "var(--se-ink)" }} data-testid={`ledger-link-${l.id}`}>{l.name}</Link></td>
                                            <td className="text-stone-500 text-xs">{l.gstin || "—"}</td>
                                            <td className="se-num">₹ {inr(balances[l.id])}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : null}
                    </div>
                    );
                })}
            </div>
        </>
    );
}
